/**
 * useNotificationPreferences Hook
 *
 * Custom React hook for managing user notification preferences.
 * Handles channel preferences (in-app, email, SMS, push) and type preferences.
 *
 * Features:
 * - Fetch user preferences
 * - Update channel preferences
 * - Update type preferences
 * - Reset to defaults
 * - Loading and error states
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * @example
 * const { preferences, updateChannels, updateTypes } = useNotificationPreferences();
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiRequest from '../../services/api';

/**
 * Default notification preferences
 */
const DEFAULT_PREFERENCES = {
  channels: {
    inApp: true,
    email: true,
    sms: false,
    push: false,
  },
  types: {
    booking: true,
    payment: true,
    message: true,
    review: true,
    system: true,
    promotional: false,
    guideApproval: true,
    adminAlert: true,
    supportTicket: true,
  },
};

/**
 * Notification preferences hook
 *
 * @returns {Object} Preferences state and methods
 */
export function useNotificationPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // ============================================================================
  // FETCH PREFERENCES
  // ============================================================================

  /**
   * Fetch user preferences from API
   */
  const fetchPreferences = useCallback(async () => {
    if (!user) {return;}

    setLoading(true);
    setError(null);

    try {
      const data = await apiRequest('/api/v1/notifications/preferences', { method: 'GET' });

      setPreferences(data.preferences);
      setInitialized(true);
    } catch (err) {
      console.error('[useNotificationPreferences] Failed to fetch preferences:', err);
      setError(err.message);

      // Use defaults on error
      setPreferences(DEFAULT_PREFERENCES);
      setInitialized(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch preferences on mount
  useEffect(() => {
    if (!initialized && user) {
      fetchPreferences();
    }
  }, [initialized, user, fetchPreferences]);

  // ============================================================================
  // UPDATE CHANNEL PREFERENCES
  // ============================================================================

  /**
   * Update channel preferences (in-app, email, SMS, push)
   * @param {Object} channels - Channel preferences
   * @param {boolean} channels.inApp - Enable in-app notifications
   * @param {boolean} channels.email - Enable email notifications
   * @param {boolean} channels.sms - Enable SMS notifications
   * @param {boolean} channels.push - Enable push notifications
   */
  const updateChannels = useCallback(
    async (channels) => {
      setLoading(true);
      setError(null);

      try {
        const data = await apiRequest('/api/v1/notifications/preferences/channels', {
          method: 'PUT',
          body: JSON.stringify(channels),
        });

        setPreferences(data.preferences);
        return { success: true };
      } catch (err) {
        console.error('[useNotificationPreferences] Failed to update channels:', err);
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ============================================================================
  // UPDATE TYPE PREFERENCES
  // ============================================================================

  /**
   * Update notification type preferences
   * @param {Object} types - Type preferences
   * @param {boolean} types.booking - Enable booking notifications
   * @param {boolean} types.payment - Enable payment notifications
   * @param {boolean} types.message - Enable message notifications
   * @param {boolean} types.review - Enable review notifications
   * @param {boolean} types.system - Enable system notifications
   * @param {boolean} types.promotional - Enable promotional notifications
   * @param {boolean} types.guideApproval - Enable guide approval notifications
   * @param {boolean} types.adminAlert - Enable admin alert notifications
   * @param {boolean} types.supportTicket - Enable support ticket notifications
   */
  const updateTypes = useCallback(
    async (types) => {
      setLoading(true);
      setError(null);

      try {
        const data = await apiRequest('/api/v1/notifications/preferences/types', {
          method: 'PUT',
          body: JSON.stringify(types),
        });

        setPreferences(data.preferences);
        return { success: true };
      } catch (err) {
        console.error('[useNotificationPreferences] Failed to update types:', err);
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ============================================================================
  // UPDATE ALL PREFERENCES
  // ============================================================================

  /**
   * Update all preferences at once
   * @param {Object} newPreferences - All preferences
   * @param {Object} newPreferences.channels - Channel preferences
   * @param {Object} newPreferences.types - Type preferences
   */
  const updateAllPreferences = useCallback(
    async (newPreferences) => {
      setLoading(true);
      setError(null);

      try {
        const data = await apiRequest('/api/v1/notifications/preferences', {
          method: 'PUT',
          body: JSON.stringify(newPreferences),
        });

        setPreferences(data.preferences);
        return { success: true };
      } catch (err) {
        console.error('[useNotificationPreferences] Failed to update preferences:', err);
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ============================================================================
  // RESET TO DEFAULTS
  // ============================================================================

  /**
   * Reset preferences to defaults
   */
  const resetToDefaults = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiRequest('/api/v1/notifications/preferences/reset', {
        method: 'POST',
      });

      setPreferences(data.preferences);
      return { success: true };
    } catch (err) {
      console.error('[useNotificationPreferences] Failed to reset preferences:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Toggle a channel on/off
   * @param {string} channel - Channel name (inApp, email, sms, push)
   */
  const toggleChannel = useCallback(
    async (channel) => {
      const currentValue = preferences.channels[channel];
      return updateChannels({ [channel]: !currentValue });
    },
    [preferences.channels, updateChannels]
  );

  /**
   * Toggle a notification type on/off
   * @param {string} type - Notification type
   */
  const toggleType = useCallback(
    async (type) => {
      const currentValue = preferences.types[type];
      return updateTypes({ [type]: !currentValue });
    },
    [preferences.types, updateTypes]
  );

  // ============================================================================
  // RETURN HOOK INTERFACE
  // ============================================================================

  return {
    // Data
    preferences,
    loading,
    error,
    initialized,

    // Methods
    fetchPreferences,
    updateChannels,
    updateTypes,
    updateAllPreferences,
    resetToDefaults,
    toggleChannel,
    toggleType,
  };
}
